import React, {useEffect, useState} from 'react';
import {Alert, Button, Divider, message, Modal, Space, Spin, Tag, Typography} from 'antd';
import {getApi, getApiPreview, updateApi} from '../api/apiApi';
import DiffViewer from './DiffViewer';

const { Text } = Typography;

function isJsonLike(v) {
  if (v == null) return false;
  if (typeof v !== 'string') return true;
  const t = v.trim();
  return (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'));
}

function jsonEqual(a, b) {
  const aa = a == null ? '' : (typeof a === 'string' ? a.trim() : a);
  const bb = b == null ? '' : (typeof b === 'string' ? b.trim() : b);
  if (aa === '' && bb === '') return true;
  const aJson = isJsonLike(aa);
  const bJson = isJsonLike(bb);
  if (aJson && bJson) {
    try {
      const ja = typeof aa === 'string' ? JSON.parse(aa) : aa;
      const jb = typeof bb === 'string' ? JSON.parse(bb) : bb;
      return JSON.stringify(ja) === JSON.stringify(jb);
    } catch (_) {}
  }
  return String(aa) === String(bb);
}

const CompareApiModal = ({ open, onClose, method, path, apiId, onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [db, setDb] = useState(null);
  const [pv, setPv] = useState(null);
  const [updating, setUpdating] = useState(false);

  const loadAll = async () => {
    if (!method || !path) return;
    setLoading(true); setError(null);
    try {
      const tasks = [];
      if (apiId) tasks.push(getApi(apiId));
      tasks.push(getApiPreview(method, path));
      const results = await Promise.allSettled(tasks);
      if (apiId) {
        const r0 = results[0];
        const r1 = results[1];
        if (r0.status === 'fulfilled') setDb(r0.value); else setError(r0.reason || new Error('상세 조회 실패'));
        if (r1.status === 'fulfilled') setPv(r1.value); else message.warning('Suggester 프리뷰 조회 실패');
      } else {
        const r0 = results[0];
        if (r0.status === 'fulfilled') setPv(r0.value); else setError(r0.reason || new Error('프리뷰 조회 실패'));
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAll();
    } else {
      setDb(null); setPv(null); setError(null); setUpdating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, method, path, apiId]);

  // DB값과 프리뷰값 추출
  const dbHeader = db?.header ?? '';
  const dbBody = db?.body ?? '';
  const dbResponse = db?.response ?? '';
  const dbName = db?.apiNm ?? '';
  const dbDesc = db?.apiDsctn ?? '';

  const pvHeader = pv?.header ?? '';
  const pvBody = pv?.body ?? '';
  const pvResponse = pv?.response ?? '';
  const pvName = pv?.apiNm ?? '';
  const pvDesc = pv?.apiDsctn ?? '';

  // 동등성
  const headerEqual = jsonEqual(dbHeader, pvHeader);
  const bodyEqual = jsonEqual(dbBody, pvBody);
  const responseEqual = jsonEqual(dbResponse, pvResponse);
  const nameEqual = String((dbName ?? '')).trim() === String((pvName ?? '')).trim();
  const descEqual = String((dbDesc ?? '')).trim() === String((pvDesc ?? '')).trim();

  const handleApply = async () => {
    if (!apiId) return;
    if (!pv) { message.warning('프리뷰가 없습니다.'); return; }
    try {
      setUpdating(true);
      await updateApi(apiId, {
        methodType: db?.methodType || method,
        apiNm: (pvName && pvName.trim()) ? pvName : (db?.apiNm || `${method} ${path}`),
        apiUrl: db?.apiUrl || path,
        apiDsctn: (pvDesc != null) ? pvDesc : (db?.apiDsctn ?? ''),
        header: pvHeader || '',
        body: pvBody || '',
        response: pvResponse || ''
      });
      message.success('갱신되었습니다.');
      const fresh = await getApi(apiId);
      setDb(fresh);
      try { const p = await getApiPreview(method, path); setPv(p); } catch (_) {}
      try { onApplied && onApplied(); } catch (_) {}
    } catch (e) {
      message.error('갱신에 실패했습니다.');
    } finally { setUpdating(false); }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      title={<Space><Typography.Text strong>API 비교</Typography.Text><Tag color="blue">{(method || '').toUpperCase()}</Tag><Text code>{path}</Text></Space>}
    >
      {loading && <Spin />}
      {error && <Alert type="error" showIcon message="비교 로드 실패" description={String(error?.message || '오류가 발생했습니다.')} style={{ marginBottom: 12 }} />}

      {!loading && !error && (
        <>
          <Space style={{ justifyContent:'flex-end', width:'100%', marginBottom: 8 }}>
            <Button onClick={loadAll}>새로고침</Button>
            {apiId && (
              <Button type="primary" loading={updating} onClick={handleApply}>갱신</Button>
            )}
          </Space>

          {/* 좌/우 레전드: DB vs 런타임 */}
          <Space style={{ justifyContent:'flex-end', width:'100%', marginBottom: 8 }}>
            <Tag color="green">DB</Tag>
            <Tag color="geekblue">런타임</Tag>
          </Space>

          <Divider orientation="left">API명 <Tag color={nameEqual ? 'green' : 'orange'} style={{ marginLeft: 8 }}>{nameEqual ? '동일' : '다름'}</Tag></Divider>
          <div style={{ marginTop: 8 }}>
            <DiffViewer left={dbName} right={pvName} leftLabel="DB API명" rightLabel="런타임 API명" />
          </div>

          <Divider orientation="left">설명 <Tag color={descEqual ? 'green' : 'orange'} style={{ marginLeft: 8 }}>{descEqual ? '동일' : '다름'}</Tag></Divider>
          <div style={{ marginTop: 8 }}>
            <DiffViewer left={dbDesc} right={pvDesc} leftLabel="DB 설명" rightLabel="런타임 설명" />
          </div>

          <Divider orientation="left">Request Header <Tag color={headerEqual ? 'green' : 'orange'} style={{ marginLeft: 8 }}>{headerEqual ? '동일' : '다름'}</Tag></Divider>
          <div style={{ marginTop: 8 }}>
            <DiffViewer left={dbHeader} right={pvHeader} leftLabel="DB Header" rightLabel="런타임 Header" />
          </div>

          <Divider orientation="left">Request Body <Tag color={bodyEqual ? 'green' : 'orange'} style={{ marginLeft: 8 }}>{bodyEqual ? '동일' : '다름'}</Tag></Divider>
          <div style={{ marginTop: 8 }}>
            <DiffViewer left={dbBody} right={pvBody} leftLabel="DB Body" rightLabel="런타임 Body" />
          </div>

          <Divider orientation="left">Response <Tag color={responseEqual ? 'green' : 'orange'} style={{ marginLeft: 8 }}>{responseEqual ? '동일' : '다름'}</Tag></Divider>
          <div style={{ marginTop: 8 }}>
            <DiffViewer left={dbResponse} right={pvResponse} leftLabel="DB Response" rightLabel="런타임 Response" />
          </div>
        </>
      )}
    </Modal>
  );
};

export default CompareApiModal;
